import { BottomNav, DesktopSidebar } from "./_shared/bottom-nav";
import { DemoProvider } from "./_shared/demo.provider";
import { PracticeProvider } from "./_shared/practice.provider";
import { PullToRefresh } from "./_shared/pull-to-refresh";
import { useRoute, type Route } from "./_shared/router";
import { SummaryPage } from "./summary/summary.page";
import { CabinetPage } from "./cabinet/cabinet.page";
import { ContactPage } from "./contact/contact.page";
import { MessagesPage } from "./messages/messages.page";
import { ChatPage } from "./messages/chat.page";
import { SearchPage } from "./search/search.page";
import { PaymentsPage } from "./details/payments.page";
import { AppointmentsPage } from "./details/appointments.page";
import { AlignersPage } from "./details/aligners.page";
import { ExercisesPage } from "./details/exercises.page";
import { PhotosPage } from "./details/photos.page";
import { SignaturesPage } from "./details/signatures.page";
import { DocumentsPage } from "./details/documents.page";
import { AstucesPage } from "./details/astuces.page";
import { BookPage } from "./book/book.page";
import { EmergencyPage } from "./emergency/emergency.page";
import { PhotoSessionsPage } from "./photo-sessions/photo-sessions.page";

const navMap: Record<Route["name"], Route["name"]> = {
  summary: "summary",
  cabinet: "cabinet",
  contact: "messages",
  messages: "messages",
  chat: "messages",
  search: "search",
  payments: "summary",
  appointments: "summary",
  aligners: "summary",
  exercises: "summary",
  photos: "summary",
  signatures: "summary",
  documents: "summary",
  astuces: "summary",
  book: "summary",
  emergency: "messages",
  "photo-sessions": "summary",
};

const fullscreenRoutes = new Set<Route["name"]>(["chat"]);

function renderPage(name: Route["name"]) {
  switch (name) {
    case "summary":
      return <SummaryPage />;
    case "cabinet":
      return <CabinetPage />;
    case "contact":
      return <ContactPage />;
    case "messages":
      return <MessagesPage />;
    case "chat":
      return <ChatPage />;
    case "search":
      return <SearchPage />;
    case "payments":
      return <PaymentsPage />;
    case "appointments":
      return <AppointmentsPage />;
    case "aligners":
      return <AlignersPage />;
    case "exercises":
      return <ExercisesPage />;
    case "photos":
      return <PhotosPage />;
    case "signatures":
      return <SignaturesPage />;
    case "documents":
      return <DocumentsPage />;
    case "astuces":
      return <AstucesPage />;
    case "book":
      return <BookPage />;
    case "emergency":
      return <EmergencyPage />;
    case "photo-sessions":
      return <PhotoSessionsPage />;
  }
}

export function PatientApp() {
  return (
    <PracticeProvider>
      <DemoProvider>
        <PatientShell />
      </DemoProvider>
    </PracticeProvider>
  );
}

function PatientShell() {
  const { route } = useRoute();
  const activeNav = navMap[route.name];
  const fullscreen = fullscreenRoutes.has(route.name);
  return (
    <div
      className={`min-h-dvh bg-[var(--bg-canvas)] text-[var(--text-1)] antialiased ${fullscreen ? "" : "pt-[env(safe-area-inset-top)]"}`}
    >
      {fullscreen ? null : <DesktopSidebar active={route.name} />}
      {fullscreen ? null : (
        <div
          aria-hidden
          className="pointer-events-none fixed inset-x-0 bottom-0 z-20 h-32 bg-linear-to-t from-[var(--bg-canvas)] via-[var(--bg-canvas)]/85 to-transparent lg:hidden"
        />
      )}
      {fullscreen ? null : <BottomNav active={activeNav} />}
      <main className={fullscreen ? "" : "lg:ml-60"}>
        {fullscreen ? (
          <div key={route.name} className="page-fade">
            {renderPage(route.name)}
          </div>
        ) : (
          <PullToRefresh>
            <div key={route.name} className="page-fade">
              {renderPage(route.name)}
            </div>
          </PullToRefresh>
        )}
      </main>
    </div>
  );
}
